import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useIntl } from 'gatsby-plugin-intl';

import SectionGraphic from './SectionGraphic';
import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import CallToAction from './CallToAction';

const Heading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading2.letterSpacing')};
  text-align: center;
  margin: 0 0 40px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
    margin: 0 0 70px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: -1.5rem auto;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    align-items: center;
    max-width: 1016px;
    padding: 0 28px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    max-width: 1072px;
  }
`;

const SubHeading = styled.h3`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading5.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading5.fontSize')};
  line-height: ${themeGet('typography.mobile.heading5.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading5.letterSpacing')};
  margin: 0 0 10px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading4.fontSize')};
    line-height: ${themeGet('typography.desktop.heading4.lineHeight')};
  }
`;

const Content = styled.div`
  max-width: 496px;
  text-align: center;
  margin: .5rem 0 0;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    max-width: 390px;
    text-align: left;
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-width: 496px;
  }
`;

const BodyCopy = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  margin: 0 0 30px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body2.fontSize')};
    line-height: ${themeGet('typography.desktop.body2.lineHeight')};
  }
`;

const Media = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 218px;
  width: 100%;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    max-width: 326px;
    margin: 0 40px 0 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-width: 362px;
  }
`;

const LocationModuleOptions = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_3]: (_, children) => <SubHeading>{children}</SubHeading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

const LocationModule = ({ data }) => {
  const intl = useIntl();
  if (!data || !data?.sectionTitle) return null;

  const {
    sectionId,
    backgroundColor,
    imageDamAsset,
    sectionTitle,
    combinatorSectionItems,
  } = data;
  const showCTA = intl.locale !== 'fr';

  return (
    <SectionGraphic
      background={backgroundColor || undefined}
      img={imageDamAsset}
      id={sectionId}
    >
      <SectionContainer>
        <RichText document={sectionTitle} options={LocationModuleOptions} />
        {combinatorSectionItems.map(
          ({
            id,
            headingRTF,
            bodyRTF,
            button,
            imageGallery,
          }) => (
            <Wrapper key={id}>
              <Media>
                {(imageGallery || [])
                  .map((image) => [image, getImage(image?.asset?.localImage)])
                  .map(([image, gatsbyImage]) => (
                    gatsbyImage ? (
                      <GatsbyImage
                        key={image.id}
                        image={gatsbyImage}
                        alt={image.altText || ''}
                      />
                    ) : (
                      <img
                        key={image.id}
                        src={image?.asset?.localImage?.publicURL || image?.asset?.url}
                        alt={image.altText || image.description || ''}
                        loading="lazy"
                      />
                    )
                  ))}
              </Media>
              <Content>
                <RichText
                  document={headingRTF}
                  options={LocationModuleOptions}
                />
                <RichText
                  document={bodyRTF}
                  options={LocationModuleOptions}
                />
                {button && showCTA && (
                  <CallToAction
                    key={button.id}
                    variant={button.buttonType}
                    href={button.buttonUrl}
                  >
                    {button.buttonLabel}
                  </CallToAction>
                )}
              </Content>
            </Wrapper>
          ),
        )}
      </SectionContainer>
    </SectionGraphic>
  );
};

LocationModule.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default LocationModule;
